import { AnimatePresence, motion } from 'motion/react';
import { memo, useCallback, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { Icon } from '../../../components/ui/icon.js';
import type { PieceImages } from '../assets/sprites.js';
import { GEM_FRAMES, VIEW_ASPECT, cellBox, cellCentre, fuseTip, gemKind, inCell, type MinesPiece, type PctBox } from './minesArt.js';

/** What one cell shows, worked out by the game view from the round. */
export interface MinesCellView {
  /** A tap is building up on it: the tile presses down before its result shows. */
  charging: boolean;
  gem: boolean;
  bomb: boolean;
  /** The bomb the player stepped on. */
  hit: boolean;
  /** After a cash-out: the bomb next to the most uncovered gems. */
  nearMiss: boolean;
  disabled: boolean;
}

interface Props {
  art: PieceImages<MinesPiece>;
  cells: MinesCellView[];
  onTile: (cell: number) => void;
  label: string;
  /** The round is lost: the tray and the tiles left on it go dark, the outcome stays lit (globals.css). */
  dim: boolean;
  reduced: boolean;
}

const at = (b: PctBox): CSSProperties => ({ position: 'absolute', left: `${b.left}%`, top: `${b.top}%`, width: `${b.width}%`, height: `${b.height}%` });
const TILE = inCell('tile');
const TILE_SHADOW = inCell('tileshadow');
const GEM = inCell('gem-pink');
const GEM_SHADOW = inCell('gemshadow');
const BOMB = inCell('bomb');
const BOMB_SHADOW = inCell('bombshadow');
const FUSE = fuseTip();

/** Sparks thrown off a cell, in percent of its tile: a fixed fan so every reveal reads the same. */
const SPARKS = [
  [-70, -60, 0.9],
  [75, -55, 0.7],
  [-10, -95, 1],
  [85, 20, 0.6],
  [-80, 25, 0.65],
] as const;

/**
 * The Mines board from its 3D render: the navy tray, a violet tile on every cell, and under
 * each tile a gem or the bomb. Each cell is a real button over its tile, so the board keeps
 * the grid's keyboard and screen-reader behaviour; the pictures inside only decorate it.
 */
export function MinesArtBoard({ art, cells, onTile, label, dim, reduced }: Props) {
  const hit = cells.findIndex((c) => c.hit);
  // One tap handler for the board's life, so a cell whose own state did not change skips the
  // re-render every tap otherwise costs all 25 of them.
  const latest = useRef(onTile);
  useLayoutEffect(() => {
    latest.current = onTile;
  });
  const tap = useCallback((cell: number) => latest.current(cell), []);
  return (
    <div data-mines-art="" data-dim={dim ? '' : undefined} className="relative w-full" style={{ aspectRatio: String(VIEW_ASPECT) }}>
      <img src={art.board.src} alt="" draggable={false} className="og-mine-board pointer-events-none absolute inset-0 h-full w-full select-none" />
      {cells.map((c, i) => (
        <ArtCell key={i} cell={i} view={c} art={art} onTile={tap} label={label} reduced={reduced} />
      ))}
      <AnimatePresence>
        {hit >= 0 && <Blast key={`blast-${hit}`} cell={hit} art={art} reduced={reduced} />}
      </AnimatePresence>
    </div>
  );
}

interface CellProps {
  cell: number;
  view: MinesCellView;
  art: PieceImages<MinesPiece>;
  onTile: (cell: number) => void;
  label: string;
  reduced: boolean;
}

const sameView = (a: MinesCellView, b: MinesCellView) =>
  a.charging === b.charging && a.gem === b.gem && a.bomb === b.bomb && a.hit === b.hit && a.nearMiss === b.nearMiss && a.disabled === b.disabled;

/** The cell's button: cheap to redraw, which every tap does to all 25 (they all lock during the build-up). */
const ArtCell = memo(
  function ArtCell({ cell, view, art, onTile, label, reduced }: CellProps) {
    return (
      <button
        type="button"
        data-tile={cell}
        disabled={view.disabled}
        onClick={() => onTile(cell)}
        aria-label={label}
        className="og-mine-cell group absolute select-none rounded-[18%] border-0 bg-transparent p-0 outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] disabled:cursor-default"
        style={at(cellBox(cell))}
      >
        <CellPicture cell={cell} charging={view.charging} gem={view.gem} bomb={view.bomb} hit={view.hit} nearMiss={view.nearMiss} art={art} reduced={reduced} />
      </button>
    );
  },
  (a, b) => a.cell === b.cell && a.art === b.art && a.onTile === b.onTile && a.label === b.label && a.reduced === b.reduced && sameView(a.view, b.view),
);

interface PictureProps {
  cell: number;
  charging: boolean;
  gem: boolean;
  bomb: boolean;
  hit: boolean;
  nearMiss: boolean;
  art: PieceImages<MinesPiece>;
  reduced: boolean;
}

/** What the cell shows; redrawn only when that changes, never for its button locking. */
const CellPicture = memo(function CellPicture({ cell, charging, gem, bomb, hit, nearMiss, art, reduced }: PictureProps) {
  const view = { charging, gem, bomb, hit, nearMiss };
  const covered = !gem && !bomb;
  const kind = gemKind(cell);
  const row = Math.floor(cell / 5);
  const col = cell % 5;
  return (
    <>
      <AnimatePresence initial={false}>
        {covered && (
          <motion.img
            key="tileshadow"
            src={art.tileshadow.src}
            alt=""
            draggable={false}
            className="pointer-events-none"
            style={at(TILE_SHADOW)}
            initial={{ opacity: 0 }}
            animate={{ opacity: view.charging ? 0.5 : 1 }}
            exit={{ opacity: 0, transition: { duration: 0.12 } }}
          />
        )}
      </AnimatePresence>

      {/* A soft glow in the socket: the pressed tile's build-up, then the stone's light. */}
      <AnimatePresence initial={false}>
        {(view.charging || view.gem) && (
          <motion.img
            key="glow"
            src={art.glow.src}
            alt=""
            draggable={false}
            className="pointer-events-none mix-blend-screen"
            style={{ ...at({ left: -25, top: -25, width: 150, height: 150 }) }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={
              view.charging && !reduced
                ? { opacity: [0.2, 0.75, 0.45, 0.9], scale: [0.7, 1, 0.9, 1.05] }
                : { opacity: view.gem ? 0.42 : 0, scale: 1 }
            }
            exit={{ opacity: 0, transition: { duration: 0.25 } }}
            transition={view.charging ? { duration: 0.3, ease: 'easeInOut' } : { duration: 0.45 }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {view.gem && (
          <motion.img
            key="gemshadow"
            src={art.gemshadow.src}
            alt=""
            draggable={false}
            className="pointer-events-none"
            style={at(GEM_SHADOW)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, delay: reduced ? 0 : 0.08 }}
          />
        )}
        {view.bomb && (
          <motion.img
            key="bombshadow"
            src={art.bombshadow.src}
            alt=""
            draggable={false}
            className="pointer-events-none"
            style={at(BOMB_SHADOW)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {view.gem && (
          <motion.div
            key="gem"
            className="pointer-events-none"
            style={{ ...at(GEM), transformOrigin: '50% 80%' }}
            initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.25, y: '35%' }}
            animate={{ opacity: 1, scale: 1, y: '0%' }}
            exit={{ opacity: 0, scale: 0.6, transition: { duration: 0.18 } }}
            transition={reduced ? { duration: 0.2 } : { type: 'spring', stiffness: 520, damping: 14, delay: 0.06 }}
          >
            <div
              className={reduced ? 'h-full w-full' : 'og-gem-spin h-full w-full'}
              style={{
                backgroundImage: `url(${art[`gem-${kind}`].src})`,
                backgroundSize: `${GEM_FRAMES * 100}% 100%`,
                backgroundRepeat: 'no-repeat',
                ['--og-gem-end' as string]: `${(GEM_FRAMES / (GEM_FRAMES - 1)) * 100}%`,
                ['--og-gem-steps' as string]: String(GEM_FRAMES),
              }}
            />
            {!reduced && (
              <img
                src={art.sparkle.src}
                alt=""
                draggable={false}
                className="og-gem-twinkle absolute"
                style={{ left: '14%', top: '-6%', width: '34%', height: '50%', animationDelay: `${((cell * 0.73) % 3.4).toFixed(2)}s` }}
              />
            )}
          </motion.div>
        )}
        {view.bomb && (
          <motion.div
            key="bomb"
            className="pointer-events-none"
            style={{ ...at(BOMB), transformOrigin: '50% 90%' }}
            initial={reduced ? { opacity: 0 } : { opacity: 0, scale: view.hit ? 1.35 : 0.5, y: view.hit ? '-30%' : '10%' }}
            animate={view.hit && !reduced ? { opacity: 1, scale: [1.35, 0.86, 1.08, 1], y: ['-30%', '4%', '-3%', '0%'], filter: ['brightness(1)', 'brightness(1)', 'brightness(0.62)', 'brightness(0.62)'] } : { opacity: 1, scale: 1, y: '0%' }}
            exit={{ opacity: 0, scale: 0.7, transition: { duration: 0.18 } }}
            transition={view.hit && !reduced ? { duration: 0.5, times: [0, 0.3, 0.6, 1] } : reduced ? { duration: 0.2 } : { type: 'spring', stiffness: 460, damping: 16 }}
          >
            <img src={art.bomb.src} alt="" draggable={false} className="h-full w-full" />
            {!view.hit && !reduced && (
              <img
                src={art.sparkle.src}
                alt=""
                draggable={false}
                className="og-fuse-spark absolute"
                style={{ left: `${FUSE.left - 22}%`, top: `${FUSE.top - 18}%`, width: '44%', height: '36%' }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {covered && (
          <motion.div
            key="tile"
            className="pointer-events-none"
            style={{ ...at(TILE), transformOrigin: '50% 60%' }}
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: '-40%', scale: 1.1 }}
            animate={
              view.charging && !reduced
                ? { opacity: 1, scale: [1, 0.93, 0.95, 0.9], y: ['0%', '4%', '3%', '6%'], x: ['0%', '-2%', '2%', '0%'] }
                : { opacity: 1, scale: 1, y: '0%', x: '0%' }
            }
            exit={
              reduced
                ? { opacity: 0, transition: { duration: 0.15 } }
                : { opacity: 0, y: '-70%', scale: 1.22, rotate: col < 2 ? -9 : 9, transition: { duration: 0.28, ease: [0.2, 0.7, 0.3, 1] } }
            }
            transition={
              view.charging
                ? { duration: 0.3, ease: 'easeInOut' }
                : { type: 'spring', stiffness: 420, damping: 22, delay: reduced ? 0 : (row + col) * 0.03 }
            }
          >
            <img
              src={art.tile.src}
              alt=""
              draggable={false}
              className="og-mine-tile h-full w-full"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* The pop as a stone comes up: sparks thrown off the cell. */}
      <AnimatePresence>
        {view.gem && !reduced && (
          <motion.div key="sparks" className="pointer-events-none absolute inset-0" initial={{ opacity: 1 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            {SPARKS.map(([dx, dy, s], k) => (
              <motion.img
                key={k}
                src={art.sparkle.src}
                alt=""
                draggable={false}
                className="absolute mix-blend-screen"
                style={{ left: '30%', top: '25%', width: '40%', height: '40%' }}
                initial={{ x: '0%', y: '0%', scale: 0, opacity: 1, rotate: 0 }}
                animate={{ x: `${dx}%`, y: `${dy}%`, scale: [0, s, 0], opacity: [1, 1, 0], rotate: 90 }}
                transition={{ duration: 0.55, delay: 0.05 + k * 0.02, ease: 'easeOut' }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {view.nearMiss && (
        <motion.span
          className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-[var(--warning)] text-[var(--bg)]"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 360, damping: 14 }}
        >
          <Icon name="bomb" size={12} />
        </motion.span>
      )}
    </>
  );
});

/** The bomb going off: a burst of fire light over the cell and a ring of sparks. */
function Blast({ cell, art, reduced }: { cell: number; art: PieceImages<MinesPiece>; reduced: boolean }) {
  const { left, top } = cellCentre(cell);
  const size = 46;
  return (
    <motion.div
      className="pointer-events-none absolute z-10"
      style={{ left: `${left - size / 2}%`, top: `${top - size / 2}%`, width: `${size}%`, height: `${size}%` }}
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.2 } }}
    >
      <motion.img
        src={art.blast.src}
        alt=""
        draggable={false}
        className="absolute inset-0 h-full w-full mix-blend-screen"
        initial={{ scale: 0.15, opacity: 1, rotate: -20 }}
        animate={reduced ? { scale: 0.8, opacity: [1, 0] } : { scale: [0.15, 1.05, 1.25], opacity: [1, 1, 0], rotate: 15 }}
        transition={{ duration: reduced ? 0.4 : 0.62, delay: reduced ? 0 : 0.1, times: reduced ? undefined : [0, 0.35, 1], ease: 'easeOut' }}
      />
      {!reduced &&
        Array.from({ length: 8 }, (_, k) => {
          const a = (k / 8) * Math.PI * 2 + 0.3;
          return (
            <motion.img
              key={k}
              src={art.sparkle.src}
              alt=""
              draggable={false}
              className="absolute mix-blend-screen"
              style={{ left: '42%', top: '42%', width: '16%', height: '16%' }}
              initial={{ x: '0%', y: '0%', scale: 0, opacity: 1 }}
              animate={{ x: `${Math.cos(a) * 320}%`, y: `${Math.sin(a) * 320}%`, scale: [0, 1.2, 0.2], opacity: [1, 1, 0] }}
              transition={{ duration: 0.7, delay: 0.14, ease: 'easeOut' }}
            />
          );
        })}
    </motion.div>
  );
}
